import React from "react";
import {
  GridLegacy as Grid,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { NotchedCard as NotchedCardV2, NotchedCardContent, NotchedCardLabel } from "../../cards/NotchedCard/NotchedCardV2";
import { useTranslation } from "react-i18next";
import { usePermissionsMatrix } from "./usePermissionsMatrix";
import { PermissionSection } from "./PermissionSection";
import RolesAndPermissionsV2 from "./RolesAndPermissionsV2";
import { PERMISSION_CATEGORIES } from "./types";
import { useRbacFlags, getFlagState } from "@/hooks/useRbacFlags";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

const RolesAndPermissionsV1: React.FC = () => {
  const { t } = useTranslation();
  const rbacFlags = useRbacFlags();
  const {
    rolesAndPermissionsData,
    permissionData,
    categorizedPermissions,
    isLoading,
    error,
    handlePermissionDataChange,
  } = usePermissionsMatrix();

  const hasDisplayableData = (categoryKey: string) => {
    const category = PERMISSION_CATEGORIES[categoryKey as keyof typeof PERMISSION_CATEGORIES];
    return category.subCategories.some(
      (subCategory) => subCategory.featureFlag && getFlagState(rbacFlags, subCategory.featureFlag)
    );
  };

  const renderPermissionContent = (categoryKey: string) => {
    if (isLoading) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {t("general.loading")}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="h6" color="error">
            {t("general.error")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {String(error)}
          </Typography>
        </Box>
      );
    }

    if (rolesAndPermissionsData && categorizedPermissions[categoryKey].length > 0 && hasDisplayableData(categoryKey)) {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(`users.rolesAndPermissions.${categoryKey}`)}
          </Typography>
          <PermissionSection
            categoryKey={categoryKey}
            permissions={categorizedPermissions[categoryKey]}
            roles={rolesAndPermissionsData.roles}
            data={permissionData}
            onDataChanged={handlePermissionDataChange}
          />
        </>
      );
    }

    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(`users.rolesAndPermissions.${categoryKey}`)}
        </Typography>
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="h6" color="text.secondary">
            🚧 Coming soon...
          </Typography>
        </Box>
      </Box>
    );
  };


  return (
    <div data-testid="roles-permissions-content">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <NotchedCardV2 data-testid="system-access-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.systemAccess")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("systemAccess")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="booking-management-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.bookingManagement")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("bookingManagement")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="insights-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.insights")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("insights")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="lane-planning-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.lanePlanning")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("lanePlanning")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="asset-tracking-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.assetTracking")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("assetTracking")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="vehicle-condition-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.vehicleCondition")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("vehicleCondition")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="buses-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.buses")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("buses")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>

        <Grid item xs={12}>
          <NotchedCardV2 data-testid="staff-management-card">
            <NotchedCardLabel>
              {t("users.rolesAndPermissions.cardTitles.staffManagement")}
            </NotchedCardLabel>
            <NotchedCardContent>
              {renderPermissionContent("staffManagement")}
            </NotchedCardContent>
          </NotchedCardV2>
        </Grid>
      </Grid>
    </div>
  );
};

const RolesAndPermissions = () => {
  const isRbacInsightsCategoriesReleased = useReleaseManagement(ReleaseFlag.releaseRbacPermissionsInsightsCategories);

  if (isRbacInsightsCategoriesReleased) {
    return <RolesAndPermissionsV2 />;
  }

  return <RolesAndPermissionsV1 />;
};

export default RolesAndPermissions;
